// Leichtgewichtige, dependency-freie i18n (DE/EN von Anfang an — Christian-Vorgabe).
// Bewusst KEINE i18n-Bibliothek: der Stringsatz ist klein und kontrolliert, die
// Supply-Chain bleibt schlank. Aufbau (Ressourcen-Dateien + Context + Hook +
// Interpolation) so gehalten, dass ein späterer Umstieg auf react-i18next mechanisch bliebe.
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import { en, type Dict } from './en';
import { de } from './de';

export type Lang = 'de' | 'en';

const RESOURCES: Record<Lang, Dict> = { de, en };
const STORAGE_KEY = 'blackfortflow.lang';

function detectLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'de' || saved === 'en') return saved;
  } catch {
    /* localStorage kann in gehärteten Kontexten fehlen — Default greift */
  }
  return typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('de')
    ? 'de'
    : 'en';
}

// Pfad-Lookup ("view.plant.title") + {var}-Interpolation. Fällt fail-safe auf den
// Key zurück (nie Crash, nie leere UI), falls ein Key mal fehlt.
function lookup(dict: Dict, key: string): string {
  const val = key.split('.').reduce<unknown>(
    (acc, part) =>
      acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[part] : undefined,
    dict,
  );
  return typeof val === 'string' ? val : key;
}

function interpolate(tpl: string, vars?: Record<string, string | number>): string {
  if (!vars) return tpl;
  return tpl.replace(/\{(\w+)\}/g, (m, name) => (name in vars ? String(vars[name]) : m));
}

export type TFunc = (key: string, vars?: Record<string, string | number>) => string;

interface I18nValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: TFunc;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }): JSX.Element {
  const [lang, setLangState] = useState<Lang>(detectLang);

  const value = useMemo<I18nValue>(() => {
    const dict = RESOURCES[lang];
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
    return {
      lang,
      setLang: (l) => {
        try {
          localStorage.setItem(STORAGE_KEY, l);
        } catch {
          /* nicht kritisch */
        }
        setLangState(l);
      },
      t: (key, vars) => interpolate(lookup(dict, key), vars),
    };
  }, [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n muss innerhalb von <I18nProvider> stehen');
  return ctx;
}
